/**
 * @file Shared WebSocket envelope frame schemas.
 * @module @labcraft/lab/shared/ws-messages
 *
 * The pty WebSocket carries two channels: binary frames (raw pty I/O
 * bytes) and JSON envelope frames (control + status). The envelope
 * shape is `{type, payload}`; this module defines the schemas for
 * each `type` so the backend validates inbound frames and the
 * frontend can build/consume them with a single source of truth.
 *
 * Frame directions:
 *
 *   resize         client → server  resize the pty
 *   focus          client → server  active objective/exercise + mentor-context
 *   probe          client → server  run one state-check probe (#1149)
 *   wrong-turn     client → server  one Tier-1 wrong-turn firing (#1150)
 *   recovered      client → server  objective satisfied after struggle (#1566)
 *   role           server → client  this connection is now driver / observer
 *   present        server → client  N connections currently attached
 *   chapter        server → client  account-global chapter pointer update
 *   mentor         server → client  one processed disposition (#1148 / #1566)
 *   mentor-pending server → client  "AI looking…" beat (#1566)
 *   probe-result   server → client  one probe's satisfied verdict (#1149)
 *
 * The `ChapterRef` schema also doubles as the `POST /api/account/me/chapter`
 * request body — a chapter pointer is a chapter pointer regardless of
 * whether it travels by HTTP write or WS push.
 */

import { z } from 'zod'
import {
  MachineLifecycleReasonSchema,
  MachineLifecycleStateSchema,
  PtyLifecycleFrameSchema,
  PtyResizeFrameSchema,
} from '@labcraft/hypervisor/shared/subscriptions'
import {
  MentorContextSchema,
  MentorDispositionSchema,
  MentorRungSchema,
} from '@labcraft/mentor/shared'

/**
 * Lifecycle-transition trigger (#1056) — the hypervisor's own enum,
 * re-exported so the SPA can map `(machine, reason)` to a recovery
 * without a second import path.
 */
export { MachineLifecycleReasonSchema }

/** Parsed `MachineLifecycleReason`. */
export type MachineLifecycleReason = z.infer<
  typeof MachineLifecycleReasonSchema
>

////////////////////////////////////////////////////////////////////////////////
// Terminal WS close codes (#1056) — one named table, both sides import
////////////////////////////////////////////////////////////////////////////////

/**
 * The app-level (4xxx) close codes lab mints on the browser-facing
 * terminal WS, named once so the service (which sends them) and the
 * SPA (which maps them to a recovery) speak the same vocabulary
 * instead of scattering magic numbers (#1056). The 4000–4099 band is
 * reconnect-suppressing per `WsSubscription`; `MACHINE_FAILED` (4005)
 * is the one code whose recovery is terminate + cold-boot rather than
 * re-attach.
 *
 * @property UNAUTHORIZED - No session / no engagement / recipe or
 *   machine not resolvable (4001).
 * @property UPSTREAM_FAILED - The hypervisor hop failed to open or
 *   dropped (4004).
 * @property MACHINE_FAILED - The VM is terminally failed (#1009 hung
 *   snapshot) — resume is impossible; offer "start a fresh shell" (4005).
 * @property SUSPENDED - The VM was snapshotted and parked while resumable
 *   (voluntary Pause, idle-suspend, or operator-suspend) — the SPA shows a
 *   parked takeover with an explicit Resume and must NOT silently reconnect
 *   (a re-attach would restore the snapshot and undo the park — #1083) (4006).
 * @property BOOT_RETRYABLE - The hypervisor failed to START the shell for a
 *   retryable boot-infrastructure reason (cgroup / network / image / restore /
 *   spawn — #1602). Distinct from a generic UPSTREAM_FAILED so the SPA shows
 *   the honest "trouble starting your shell" surface immediately instead of
 *   counting three 4004s; still reconnect-suppressed by the 4xxx band (4007).
 * @property BUDGET_EXHAUSTED - The learner is over the daily VM budget
 *   (#834); the SPA renders the upgrade CTA (4403).
 * @property AT_CAPACITY - The host is full and the learner has been queued
 *   (umbrella #1257 Part D, #1283); the SPA renders the at-capacity /
 *   enable-notifications overlay (Part E, #1284). 4429 mirrors HTTP 429.
 */
export const TERMINAL_CLOSE_CODES = {
  UNAUTHORIZED: 4001,
  UPSTREAM_FAILED: 4004,
  MACHINE_FAILED: 4005,
  SUSPENDED: 4006,
  BOOT_RETRYABLE: 4007,
  BUDGET_EXHAUSTED: 4403,
  AT_CAPACITY: 4429,
} as const

/** One of the app-level terminal close codes. */
export type TerminalCloseCode =
  (typeof TERMINAL_CLOSE_CODES)[keyof typeof TERMINAL_CLOSE_CODES]

////////////////////////////////////////////////////////////////////////////////
// Resize (client → server) — hypervisor wire schema, re-exported
////////////////////////////////////////////////////////////////////////////////

/**
 * `{type:'resize', payload:{cols, rows}}` envelope — the hypervisor's
 * own `PtyResizeFrameSchema` (umbrella #1048 / #1050): lab forwards
 * the frame verbatim, so both wire hops validate against the one
 * schema instead of a hand-mirrored copy.
 */
export const ResizeFrameSchema = PtyResizeFrameSchema

/** Parsed `resize` envelope. */
export type ResizeFrame = z.infer<typeof ResizeFrameSchema>

/** Payload of a `resize` frame: viewport columns + rows in cells. */
export const ResizePayloadSchema = ResizeFrameSchema.shape.payload

/** Parsed `resize` payload. */
export type ResizePayload = z.infer<typeof ResizePayloadSchema>

////////////////////////////////////////////////////////////////////////////////
// Role (server → client)
////////////////////////////////////////////////////////////////////////////////

/**
 * Roles a connection can hold within its user's VM presence:
 * `driver` (input flows to the pty) or `observer` (input is dropped;
 * output is broadcast). Per ADR-006 (e).
 */
export const RoleSchema = z.enum(['driver', 'observer'])

/** Parsed `role` literal. */
export type Role = z.infer<typeof RoleSchema>

/** Payload of a `role` frame. */
export const RolePayloadSchema = z.object({ role: RoleSchema })

/** Parsed `role` payload. */
export type RolePayload = z.infer<typeof RolePayloadSchema>

/** Full `{type:'role', payload:{role}}` envelope. */
export const RoleFrameSchema = z.object({
  type: z.literal('role'),
  payload: RolePayloadSchema,
})

/** Parsed `role` envelope. */
export type RoleFrame = z.infer<typeof RoleFrameSchema>

////////////////////////////////////////////////////////////////////////////////
// Chapter ref (POST /api/account/me/chapter request body)
////////////////////////////////////////////////////////////////////////////////

/**
 * Pointer to a chapter in the catalog. Per ADR-006 (g) this is
 * account-global (not per-tab) — the same shape used by the
 * frontend's `react-router` chapter URLs and by `course-kit`'s
 * `ChapterRef`. Wire format and content-kit type are deliberately
 * structurally identical so they're interchangeable at boundaries.
 *
 * #1060 retired the `{type:'chapter'}` server-push envelope (no
 * producer, no consumer — the cross-tab broadcast it presupposed is an
 * unbuilt ADR-006 (g) follow-up); this ref survives as the
 * `POST /api/account/me/chapter` request body.
 */
export const ChapterRefSchema = z.object({
  courseId: z.string().min(1),
  partId: z.string().min(1),
  chapterId: z.string().min(1),
})

/** Parsed chapter ref. */
export type ChapterRef = z.infer<typeof ChapterRefSchema>

////////////////////////////////////////////////////////////////////////////////
// `lifecycle` envelope — server → client (forwarded from hypervisor)
////////////////////////////////////////////////////////////////////////////////

/**
 * VM lifecycle state — the SPA's render branch is keyed off this.
 * The hypervisor's wire enum, imported directly (umbrella #1048 /
 * #1050) so a new lifecycle state touches exactly one package — the
 * pre-#1050 hand-mirror silently dropped `failed` on the admin copy
 * and was one bump away from doing the same here.
 *
 * Absence (`null`) renders no overlay. The SPA's render vocabulary is
 * coarser — groups boot-path states under one spinner, pause-path
 * states under one "Pausing" dim. See the canonical enum docs in
 * `ts/services/hypervisor/src/shared/subscriptions.ts`.
 */
export { MachineLifecycleStateSchema }

/** Parsed `MachineLifecycleState`. */
export type MachineLifecycleState = z.infer<typeof MachineLifecycleStateSchema>

/**
 * Lifecycle frame — lab forwards this from the hypervisor's
 * `PtyLifecycleFrameSchema` (the schema IS the hypervisor's, imported;
 * `expiresAt` is stamped by lab from the engagement TTL on the way
 * through, a slot the wire schema already carries).
 */
export const LifecycleFrameSchema = PtyLifecycleFrameSchema

/** Parsed `lifecycle` envelope. */
export type LifecycleFrame = z.infer<typeof LifecycleFrameSchema>

////////////////////////////////////////////////////////////////////////////////
// `engagement` envelope — server → client (umbrella #1048 / #1059)
////////////////////////////////////////////////////////////////////////////////

/**
 * `engagement` frame — lab-origin session metadata, sent on its OWN
 * frame rather than mutated onto a forwarded lifecycle frame (#1059).
 *
 * Pre-#1059 lab spread `expiresAt` onto every hypervisor lifecycle
 * frame via a type cast — a lab-authored field riding a schema the lab
 * "forwards verbatim." That coupling is retired: the hypervisor's
 * lifecycle frames now splice through untouched, and lab emits this
 * frame to carry the one datum it owns — the engagement-TTL deadline.
 *
 * `expiresAt` is `Date.now() + engagementTtl` at send time. The TTL
 * slides on every Redis touch, so the current deadline is always
 * `now + idleTtl`; lab sends this frame on bridge open and again
 * whenever it forwards a lifecycle transition (each of which implies a
 * refreshed deadline). The SPA reads it to drive the pre-expiry warning
 * banner once `expiresAt - now < 5min`.
 *
 * @property type - Literal `'engagement'` (discriminant).
 * @property expiresAt - Epoch-ms engagement-TTL deadline.
 */
export const EngagementFrameSchema = z.object({
  type: z.literal('engagement'),
  expiresAt: z.number().int().nonnegative(),
})

/** Parsed `engagement` envelope. */
export type EngagementFrame = z.infer<typeof EngagementFrameSchema>

////////////////////////////////////////////////////////////////////////////////
// `budget-exhausted` envelope — server → client (umbrella #926 / #834 seam)
////////////////////////////////////////////////////////////////////////////////

/**
 * Payload of a `budget-exhausted` frame: where the SPA sends the
 * learner to lift the cap.
 *
 * @property upgradeUrl - Absolute URL of the "go unlimited" upgrade
 *   flow. The conversion CTA the resume hero renders.
 */
export const BudgetExhaustedPayloadSchema = z.object({
  upgradeUrl: z.string().url(),
})

/** Parsed `budget-exhausted` payload. */
export type BudgetExhaustedPayload = z.infer<
  typeof BudgetExhaustedPayloadSchema
>

/**
 * `budget-exhausted` frame — the wire contract for umbrella #926's
 * conversion moment. Emitted by the daily-budget terminate path
 * (umbrella #834) **after** it snapshots live VM state via the
 * hypervisor's `suspend(id)` seam (which, post-#926, captures live
 * state on every terminate path) and **before** it tears the
 * engagement down. The SPA renders the upgrade CTA; the next resume —
 * same day on a paid plan, or after the learner upgrades — restores
 * the snapshot mid-chapter, exactly where they hit the wall.
 *
 * Defined here as the shape both sides agree on; the emitter (#834's
 * budget-terminate) and the `dispatchEnvelope` consumer branch land
 * with that umbrella so the validated terminal-frame union never
 * carries an unreachable case ahead of its producer.
 *
 * @property type - Literal `'budget-exhausted'` (discriminant).
 * @property payload - {@link BudgetExhaustedPayload}.
 */
export const BudgetExhaustedFrameSchema = z.object({
  type: z.literal('budget-exhausted'),
  payload: BudgetExhaustedPayloadSchema,
})

/** Parsed `budget-exhausted` envelope. */
export type BudgetExhaustedFrame = z.infer<typeof BudgetExhaustedFrameSchema>

/**
 * Payload of the `at-capacity` frame (umbrella #1257 Part E, #1284) — the host
 * is full and the learner has been queued. Sent on the terminal WS immediately
 * before it closes with `AT_CAPACITY` (4429), so the SPA renders the
 * at-capacity / enable-notifications overlay with the learner's place in line.
 * The "your spot is ready" call-back is delivered out-of-band by Web Push (the
 * WS is closed by then).
 *
 * @property position - 0-based queue position (0 = next to be admitted).
 * @property pushEnabled - Whether the daemon has a VAPID keypair provisioned,
 *   so the overlay knows whether to offer the enable-notifications opt-in.
 */
export const AtCapacityPayloadSchema = z.object({
  position: z.number().int().nonnegative(),
  pushEnabled: z.boolean(),
})

/** Parsed at-capacity payload. */
export type AtCapacityPayload = z.infer<typeof AtCapacityPayloadSchema>

/**
 * `at-capacity` server envelope (umbrella #1257 Part E, #1284).
 *
 * @property type - Literal `'at-capacity'` (discriminant).
 * @property payload - {@link AtCapacityPayload}.
 */
export const AtCapacityFrameSchema = z.object({
  type: z.literal('at-capacity'),
  payload: AtCapacityPayloadSchema,
})

/** Parsed `at-capacity` envelope. */
export type AtCapacityFrame = z.infer<typeof AtCapacityFrameSchema>

////////////////////////////////////////////////////////////////////////////////
// `command` envelope — server → client (umbrella #156 Phase 1b, #628)
////////////////////////////////////////////////////////////////////////////////

/**
 * Payload of a `command` frame: one completed command observed on the
 * Machine's pty via OSC 133 (#627), forwarded by lab from the
 * hypervisor's `GateSub` feed. Lean by design (#1144): the browser
 * needs only what advancement consumes — the learner already sees the
 * output in the terminal, and the mentor's full-output path is a
 * separate lab→mentor relay that never crosses the browser WS.
 *
 * @property cmd - The command line the learner ran.
 * @property exit - Exit code.
 * @property cwd - Working directory at command time.
 */
export const CommandPayloadSchema = z.object({
  cmd: z.string(),
  exit: z.number().int(),
  cwd: z.string(),
})

/** Parsed `command` payload. */
export type CommandPayload = z.infer<typeof CommandPayloadSchema>

/**
 * `command` frame — lab → browser command-event dispatch (#628). Lab
 * subscribes to the Machine's `GateSub` and forwards each command
 * event on the terminal WS as this lean frame; the browser-side
 * course-kit scanner (#629) advances objectives on it.
 *
 * @property type - Literal `'command'` (discriminant).
 * @property payload - {@link CommandPayload}.
 */
export const CommandFrameSchema = z.object({
  type: z.literal('command'),
  payload: CommandPayloadSchema,
})

/** Parsed `command` envelope. */
export type CommandFrame = z.infer<typeof CommandFrameSchema>

////////////////////////////////////////////////////////////////////////////////
// `focus` envelope — client → server (#1148 mentor relay)
////////////////////////////////////////////////////////////////////////////////

/**
 * Payload of a `focus` frame: the SPA names the entity the learner is
 * actively working on — an objective, or (once every objective is
 * complete) the exercise being proven — plus its authored mentor-context.
 * The browser owns the chapter spec and the active-entity state, and
 * the mentor payload is course-authored content, so relaying it upward
 * is trust-neutral: lab forwards it verbatim to the mentor daemon as the
 * session's `context` frame and never interprets it.
 *
 * The `mentor` sub-shape IS the mentor daemon's own `MentorContextSchema`
 * (imported from `@labcraft/mentor/shared`) — both wire hops validate
 * against the one schema instead of a hand-mirrored copy, same pattern
 * as the hypervisor's `resize` / `resume` frames above.
 *
 * @property objectiveId - Opaque key for the active objective or
 *   exercise. Scopes the mentor's restraint ladder; never parsed.
 * @property description - Human-readable statement of what the learner
 *   is trying to accomplish. Fed to the mentor's model verbatim.
 * @property mentor - Authored mentoring payload
 *   (`{rationale, misconceptions, guidance?}`).
 */
export const FocusPayloadSchema = z.object({
  objectiveId: z.string().min(1),
  description: z.string(),
  mentor: MentorContextSchema,
})

/** Parsed `focus` payload. */
export type FocusPayload = z.infer<typeof FocusPayloadSchema>

/**
 * `focus` frame — browser → lab mentor-context arming (#1148). The
 * bridge remembers the latest focus per connection and forwards it to
 * the per-engagement mentor session as a `context` frame; observations
 * relayed before any focus are dropped (the mentor has nothing to judge
 * them against). This frame terminates at lab — it is never forwarded
 * to the hypervisor.
 *
 * @property type - Literal `'focus'` (discriminant).
 * @property payload - {@link FocusPayload}.
 */
export const FocusFrameSchema = z.object({
  type: z.literal('focus'),
  payload: FocusPayloadSchema,
})

/** Parsed `focus` envelope. */
export type FocusFrame = z.infer<typeof FocusFrameSchema>

////////////////////////////////////////////////////////////////////////////////
// `mentor` / `mentor-pending` envelopes — server → client (#1148 / #1566)
////////////////////////////////////////////////////////////////////////////////

/**
 * Payload of a `mentor` frame: one processed observation's disposition,
 * relayed mentor → lab → SPA so the dock can build its per-objective
 * transcript + honest pending states (#1566). Generalizes the former
 * nudge-only frame — the reader keys the dock off `disposition`: `held`
 * ("AI standing by", an authored hook covers it), `nudge` /
 * `encouragement` (append a transcript entry — the latter is the
 * success-after-struggle capstone), `silent` (quiet). `budget_exceeded` /
 * `error` also ride this frame so a pending "AI looking…" beat always
 * resolves; the reader treats them as quiet.
 *
 * @property objectiveId - The objective/exercise the observation was
 *   judged against (the active focus's key at model-call time).
 * @property disposition - What the mentor did with the observation
 *   (`MentorDispositionSchema`, imported from `@labcraft/mentor/shared`).
 * @property rung - Restraint-ladder rung (1..3); present iff
 *   `disposition === 'nudge'`. The SPA renders escalating labels off it.
 * @property text - Learner-facing text — Socratic nudge, or the #1566
 *   encouragement + why-it-worked. Present on any text-bearing disposition
 *   (`nudge` + `encouragement`). Plain text — renderers must NOT treat it
 *   as HTML.
 */
export const MentorFramePayloadSchema = z.object({
  objectiveId: z.string().min(1),
  disposition: MentorDispositionSchema,
  rung: MentorRungSchema.optional(),
  text: z.string().min(1).optional(),
})

/** Parsed `mentor` payload. */
export type MentorFramePayload = z.infer<typeof MentorFramePayloadSchema>

/**
 * `mentor` frame — mentor → lab → SPA disposition relay (#1148 / #1566).
 * The side-branch topology holds: the mentor hangs off lab and never
 * touches the hypervisor, so a slow or down mentor cannot break the
 * terminal or advancement — the whole channel is advisory, asynchronous,
 * and never gates.
 *
 * @property type - Literal `'mentor'` (discriminant).
 * @property payload - {@link MentorFramePayload}.
 */
export const MentorFrameSchema = z.object({
  type: z.literal('mentor'),
  payload: MentorFramePayloadSchema,
})

/** Parsed `mentor` envelope. */
export type MentorFrame = z.infer<typeof MentorFrameSchema>

/**
 * Payload of a `mentor-pending` frame: the lab-owned "AI looking…" beat
 * (#1566). Minted only when the lab actually relays an observation to the
 * mentor (never on a dropped / empty / repeat observation), so the dock
 * shows a pending state that a real model call is coming — resolved by the
 * matching `mentor` frame. The mentor never echoes this; it is purely
 * lab-originated honest-pending signalling.
 *
 * @property objectiveId - The active objective the pending beat scopes to.
 */
export const MentorPendingPayloadSchema = z.object({
  objectiveId: z.string().min(1),
})

/** Parsed `mentor-pending` payload. */
export type MentorPendingPayload = z.infer<typeof MentorPendingPayloadSchema>

/**
 * `mentor-pending` frame — lab → SPA honest-pending beat (#1566).
 *
 * @property type - Literal `'mentor-pending'` (discriminant).
 * @property payload - {@link MentorPendingPayload}.
 */
export const MentorPendingFrameSchema = z.object({
  type: z.literal('mentor-pending'),
  payload: MentorPendingPayloadSchema,
})

/** Parsed `mentor-pending` envelope. */
export type MentorPendingFrame = z.infer<typeof MentorPendingFrameSchema>

////////////////////////////////////////////////////////////////////////////////
// `recovered` envelope — client → server (#1566 recovery signal)
////////////////////////////////////////////////////////////////////////////////

/**
 * Payload of a `recovered` frame: the reader's scanner matched a command
 * that satisfied the active objective (#1566). Only the browser-side
 * scanner knows a command *satisfied* an objective (the regex match); the
 * mentor sees raw commands and can't. The reader emits this when a
 * satisfying command lands on an objective whose transcript already holds
 * a struggle entry, and the lab relays it to the mentor as a
 * `progress:satisfied` recovery observation **iff** the operator's
 * `mentorEncouragementEnabled` setting is on — the mentor then generates
 * the encouragement + why-the-approach-worked.
 *
 * @property objectiveId - The objective the satisfying command completed
 *   (same opaque key space as `focus`).
 * @property cmd - The command that satisfied the objective, so the
 *   encouragement can name why that approach worked.
 */
export const RecoveredPayloadSchema = z.object({
  objectiveId: z.string().min(1),
  cmd: z.string(),
})

/** Parsed `recovered` payload. */
export type RecoveredPayload = z.infer<typeof RecoveredPayloadSchema>

/**
 * `recovered` frame — browser → lab recovery signal (#1566). Like `focus`
 * / `wrong-turn`, this frame terminates at lab: the lab marks the
 * episode's capture rows `RECOVERED` and, gated on the encouragement
 * setting, relays it to the per-engagement mentor session. Never forwarded
 * to the hypervisor; fire-and-forget, failure-tolerant per the #1148
 * mentor-branch posture.
 *
 * @property type - Literal `'recovered'` (discriminant).
 * @property payload - {@link RecoveredPayload}.
 */
export const RecoveredFrameSchema = z.object({
  type: z.literal('recovered'),
  payload: RecoveredPayloadSchema,
})

/** Parsed `recovered` envelope. */
export type RecoveredFrame = z.infer<typeof RecoveredFrameSchema>

////////////////////////////////////////////////////////////////////////////////
// `wrong-turn` envelope — client → server (#1150; lab-side capture #1432)
////////////////////////////////////////////////////////////////////////////////

/**
 * Payload of a `wrong-turn` frame: one deterministic Tier-1 wrong turn
 * that fired browser-side (course-kit's `rethink` dispatch, surfaced
 * through the `objective-rethink` funnel event). Pure aggregate
 * telemetry (#1150) — no command text, no learner data on the frame;
 * both ids are authored course content.
 *
 * @property objectiveId - The objective/exercise the wrong turn is
 *   scoped to (same opaque key space as `focus`).
 * @property wrongTurnId - The authored `WrongTurn.id` that fired.
 */
export const WrongTurnPayloadSchema = z.object({
  objectiveId: z.string().min(1),
  wrongTurnId: z.string().min(1),
})

/** Parsed `wrong-turn` payload. */
export type WrongTurnPayload = z.infer<typeof WrongTurnPayloadSchema>

/**
 * `wrong-turn` frame — browser → lab Tier-1 hit report (#1150). Like
 * `focus`, this frame terminates at lab: the bridge persists it at the
 * source into `mentor_wrong_turns` (#1432; the durable retirement /
 * re-validation signal for promoted + authored rules, read by the
 * recalibration proposer) and, best-effort, forwards it to the
 * per-engagement mentor session as a `wrongTurn` frame. Never forwarded
 * to the hypervisor; fire-and-forget, failure-tolerant per the #1148
 * mentor-branch posture — the capture lands even if the mentor is down.
 *
 * @property type - Literal `'wrong-turn'` (discriminant).
 * @property payload - {@link WrongTurnPayload}.
 */
export const WrongTurnFrameSchema = z.object({
  type: z.literal('wrong-turn'),
  payload: WrongTurnPayloadSchema,
})

/** Parsed `wrong-turn` envelope. */
export type WrongTurnFrame = z.infer<typeof WrongTurnFrameSchema>

////////////////////////////////////////////////////////////////////////////////
// `probe` / `probe-result` envelopes — state-goal probes (#1149)
////////////////////////////////////////////////////////////////////////////////

/**
 * Payload of a `probe` frame: one `{via:'state'}` detector evaluation
 * request (#1149). The browser's probe scheduler owns the trigger +
 * rate-limit policy; lab's bridge stays dumb — it relays the check to
 * the hypervisor's out-of-band probe RPC (#1147) and answers with a
 * `probe-result` carrying the same `id`.
 *
 * @property id - Opaque request id the browser mints
 *   (`crypto.randomUUID()`); correlates the async `probe-result`.
 * @property check - Author-trusted state-check script (course content
 *   from the rootfs trust domain — the SPA only ever sends checks the
 *   chapter spec authored; ADR-019 2026-07-11 amendment).
 */
export const ProbePayloadSchema = z.object({
  id: z.string().min(1),
  check: z.string().min(1),
})

/** Parsed `probe` payload. */
export type ProbePayload = z.infer<typeof ProbePayloadSchema>

/**
 * `probe` frame — browser → lab state-check dispatch (#1149). Like
 * `focus`, this frame terminates at lab: the bridge calls the
 * hypervisor's HTTP probe route for its vmId; nothing rides the pty WS
 * upstream.
 *
 * @property type - Literal `'probe'` (discriminant).
 * @property payload - {@link ProbePayload}.
 */
export const ProbeFrameSchema = z.object({
  type: z.literal('probe'),
  payload: ProbePayloadSchema,
})

/** Parsed `probe` envelope. */
export type ProbeFrame = z.infer<typeof ProbeFrameSchema>

/**
 * Payload of a `probe-result` frame: the verdict for one `probe`
 * request. Reduced to a boolean at the bridge — the browser's
 * scheduler only branches on satisfied/not (exit code and output tail
 * stay server-side, in lab's logs, for operator debugging).
 *
 * @property id - The originating `probe` frame's request id.
 * @property satisfied - Whether the check passed (`exit === 0`).
 *   Delivery failures (machine unknown, agent channel down, timeout)
 *   all read as `false` — a probe error never gates or crashes
 *   advancement, it just doesn't advance it.
 */
export const ProbeResultPayloadSchema = z.object({
  id: z.string().min(1),
  satisfied: z.boolean(),
})

/** Parsed `probe-result` payload. */
export type ProbeResultPayload = z.infer<typeof ProbeResultPayloadSchema>

/**
 * `probe-result` frame — lab → browser probe verdict (#1149). The
 * request/response pair over the terminal WS mirrors how `focus` /
 * `nudge` ride the same socket: one wire, typed envelopes, no second
 * connection for the advancement path.
 *
 * @property type - Literal `'probe-result'` (discriminant).
 * @property payload - {@link ProbeResultPayload}.
 */
export const ProbeResultFrameSchema = z.object({
  type: z.literal('probe-result'),
  payload: ProbeResultPayloadSchema,
})

/** Parsed `probe-result` envelope. */
export type ProbeResultFrame = z.infer<typeof ProbeResultFrameSchema>

/**
 * Serialize a `ChapterRef` to its DB storage form
 * (`<courseId>/<partId>/<chapterId>`). Each field is a URL slug
 * (no `/` allowed in any segment), so a slash-separated form is
 * unambiguous to parse.
 */
export function serializeChapterRef(ref: ChapterRef): string {
  return `${ref.courseId}/${ref.partId}/${ref.chapterId}`
}

/**
 * Parse a slash-separated chapter ref out of the DB column. Returns
 * null on any shape mismatch (wrong number of segments, empty
 * segment) so callers can treat malformed legacy values as "no
 * pointer set" rather than crashing.
 */
export function parseChapterRef(raw: string | null): ChapterRef | null {
  if (!raw) return null
  const parts = raw.split('/')
  if (parts.length !== 3) return null
  const [courseId, partId, chapterId] = parts
  if (!courseId || !partId || !chapterId) return null
  return { courseId, partId, chapterId }
}
